import { useMemo } from 'react';
import useTheme from '../../hooks/ui/useTheme';
import { useSelector } from 'react-redux';
import InfoIcon from '../../assets/logo/InfoIcon';
import PicturesSlider from './PicturesSlider';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import BackIcon from '../../assets/logo/BackIcon';
import { StoreInterface } from '../../store/types';
import { useNavigate } from 'react-router-dom';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import classNames from 'classnames';
import { DeliveryMode, ORDER_METHODS } from '../../services/exports/Constants';

interface Props {
  openInfoModal: () => void;
}

export default function CompanyInfo(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Company:CompanyInfo' });

  const { openInfoModal } = props;

  const { formatCurrency } = useAppMethods();

  const { franchise, company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const navigate = useNavigate();
  const theme = useTheme();
  const openingStatus = useOpeningStatus();

  function renderAddress() {
    const address = company?.address + (company?.city ? `, ${company?.city}` : '');

    return <p className="text-sm text-gray-700 mt-xs">{address}</p>;
  }

  function renderOpeningHours() {
    return (
      <div className="flex mt-xs items-center justify-center lg:justify-start">
        <p className="text-sm text-gray-700">{openingStatus.details_text}</p>
        <div className="flex ml-sm items-center cursor-pointer" onClick={openInfoModal}>
          <p className="text-sm text-gray-700 mr-2xs font-medium">{t('more_info')}</p>
          <InfoIcon color={theme.color.gray['300']} />
        </div>
      </div>
    );
  }

  const renderOrderMethodInfo = useMemo<JSX.Element>(() => {
    const renderBadge = (text: string) => (
      <div className="flex bg-gray-500 p-xs py-2xs rounded-xl">
        <span className="text-xs text-gray-700 align-middle my-auto">{text}</span>
      </div>
    );

    const isDelivery = cached_order?.method === ORDER_METHODS.DELIVERY;
    const usesExternalDelivery = company?.delivery_mode === DeliveryMode.External;

    const fee = isDelivery
      ? usesExternalDelivery
        ? cached_order?.delivery_quote?.customer_price
        : cached_order?.delivery_zone?.fee
      : 0;

    const minOrder = isDelivery ? cached_order?.delivery_zone?.min_threshold : 0;

    return (
      (Boolean(fee) || Boolean(minOrder)) && (
        <div className="flex mt-xs items-center justify-center lg:justify-start space-x-2">
          {fee ? renderBadge(t(`order_method_info.fee.${cached_order?.method}`, { fee: formatCurrency(fee) })) : null}
          {minOrder ? renderBadge(t(`order_method_info.min_order`, { minOrder: formatCurrency(minOrder) })) : null}
        </div>
      )
    );
  }, [
    cached_order?.method,
    cached_order?.delivery_zone?.fee,
    cached_order?.delivery_zone?.min_threshold,
    cached_order?.delivery_quote?.customer_price,
  ]);

  const renderImageCarousel = useMemo<JSX.Element>(() => {
    const pictureLength: number = company?.pictures?.length ?? 0;

    return (
      <div className="relative w-full lg:h-fit">
        <div className="relative lg:h-fit h-[150px]">
          {pictureLength > 0 && (
            <PicturesSlider
              pictures={company?.pictures}
              className={classNames('lg:rounded-xl rounded-none overflow-hidden lg:h-[270px]', {
                'lg:h-[300px]': franchise?.has_multiple_companies,
              })}
            />
          )}
        </div>
      </div>
    );
  }, [company?.pictures, franchise?.has_multiple_companies]);

  return (
    <div className="h-fit w-full">
      <div className="w-full relative flex flex-col justify-center">
        {renderImageCarousel}
        <div className={infoContainer}>
          <h1 className="lg:mr-md font-medium leading-6 text-lg">{company?.name}</h1>
          {franchise?.has_multiple_companies && (
            <div
              className="flex mt-xs cursor-pointer justify-center lg:justify-start"
              onClick={() => navigate('/', { replace: true })}
            >
              <h6 className="text-sm my-auto mr-2xs text-gray-700">{t('buttons.change_store')}</h6>
              <BackIcon color={theme.color.gray['300']} className="rotate-180 h-3 !w-3 my-auto" />
            </div>
          )}
          {renderAddress()}
          {renderOpeningHours()}
          {renderOrderMethodInfo}
        </div>
      </div>
    </div>
  );
}

const infoContainer =
  'lg:ml-md p-md lg:pr-2xl lg:absolute relative justify-center flex flex-col z-10 lg:border-none lg:shadow-section lg:mt-none lg:max-w-1/2 lg:w-fit lg:bg-white-100 lg:rounded-xl text-center lg:text-left';
